import { PropertyList, type PropertyListProps } from '@app/components'
import { normalizeSocialUrl, SOCIAL_NETWORKS, weekDays } from '@app/lib'
import { useSugarContext } from '@app/providers'
import { type SugarProviderProfile } from '@app/types'
import { Anchor, Group, Stack, Text } from '@mantine/core'
import dayjs from 'dayjs'
import { join, map, split } from 'lodash'
import { type FC } from 'react'

import { SocialMediaIconRow } from './SocialMediaIconRow'

export type ProviderProfileSection = 'all' | 'general' | 'contact' | 'curriculum' | 'schedule'

export interface ProviderProfilePropertyListProps extends Omit<PropertyListProps, 'items'> {
  providerProfile: SugarProviderProfile | null | undefined
  section?: ProviderProfileSection
}

export const ProviderProfilePropertyList: FC<ProviderProfilePropertyListProps> = ({
  providerProfile,
  section = 'all',
  ...props
}) => {
  const { getOptionLabelByValue } = useSugarContext()
  const socialMedia = providerProfile?.socialMedia
  const hasAnySocial = socialMedia
    ? SOCIAL_NETWORKS.some(({ network }) => normalizeSocialUrl(network, socialMedia[network]).canonical !== '')
    : false

  const includeGeneral = section === 'all' || section === 'general'
  const includeContact = section === 'all' || section === 'contact'
  const includeCurriculum = section === 'all' || section === 'curriculum'
  const includeSchedule = section === 'all' || section === 'schedule'

  const items: PropertyListProps['items'] = []

  if (includeGeneral) {
    items.push(
      { label: 'Program Name', value: providerProfile?.programName },
      { label: 'Program Description', value: providerProfile?.programDescription },
      { label: 'Director Name', value: providerProfile?.directorName },
      { label: 'License Name', value: providerProfile?.licenseName },
      { label: 'License Type', value: providerProfile?.licenseType },
      { label: 'License Number', value: providerProfile?.licenseNumber },
      { label: 'Participation Levels', value: providerProfile?.participationLevels },
      {
        label: 'Ages Served',
        value: providerProfile?.agesServed
          ? map(split(providerProfile.agesServed, ','), (value, key) => (
              <Text
                key={key}
                size='sm'
              >
                {value}
              </Text>
            ))
          : null
      }
    )
  }

  if (includeContact) {
    items.push(
      { label: 'Address', value: providerProfile?.address },
      {
        label: 'Contact Name',
        value: providerProfile && `${providerProfile?.contactFirstName} ${providerProfile?.contactLastName}`
      },
      {
        label: 'Contact Phone',
        value: providerProfile?.contactPhone ? (
          <Anchor
            href={`tel:${providerProfile.contactPhone}`}
            size='sm'
          >
            {providerProfile.contactPhone}
          </Anchor>
        ) : (
          '-'
        )
      },
      {
        label: 'Contact Email',
        value: providerProfile?.contactEmail ? (
          <Anchor
            href={`mailto:${providerProfile.contactEmail}`}
            size='sm'
          >
            {providerProfile.contactEmail}
          </Anchor>
        ) : (
          '-'
        )
      },
      {
        label: 'School Website',
        value: providerProfile?.website ? (
          <Anchor
            href={providerProfile.website}
            size='sm'
          >
            {providerProfile.website}
          </Anchor>
        ) : (
          '-'
        )
      },
      {
        label: 'Social Media',
        value:
          hasAnySocial && socialMedia ? (
            <SocialMediaIconRow
              values={socialMedia}
              variant='compact'
            />
          ) : (
            '-'
          )
      }
    )
  }

  if (includeCurriculum) {
    items.push(
      {
        label: 'Financial Assistance Available',
        value: (
          <Text size='sm'>
            {join(
              map(providerProfile?.financialAssistanceAvailable, (value) =>
                getOptionLabelByValue('programSubsidiesAccepted', value)
              ),
              ', '
            )}
          </Text>
        )
      },
      { label: 'Quality Rating Finder', value: providerProfile?.qualityRating },
      { label: 'Quality Rating (Tuition Credit)', value: providerProfile?.qualityRatingTuitionCredit },
      {
        label: 'Curriculum',
        value: (
          <Text size='sm'>
            {join(
              map(providerProfile?.curriculum, (curriculum) => getOptionLabelByValue('curriculum', curriculum)),
              ', '
            )}
          </Text>
        )
      },
      {
        label: 'Languages Spoken',
        value: (
          <Text size='sm'>
            {join(
              map(providerProfile?.languagesSpoken, (language) => getOptionLabelByValue('languagesSpoken', language)),
              ', '
            )}
          </Text>
        )
      }
    )
  }

  if (includeSchedule) {
    items.push(
      {
        label: 'Program Season',
        value: providerProfile?.programSeason
          ? getOptionLabelByValue('programSeason', providerProfile.programSeason)
          : '-'
      },
      {
        label: 'Operating Hours',
        value: (
          <Stack spacing={0}>
            {map(providerProfile?.operatingDays, (operatingDay) =>
              operatingDay.startTime && operatingDay.endTime ? (
                <Group
                  align='center'
                  key={`day-${operatingDay.dayIndex}`}
                  spacing='xs'
                >
                  <Text
                    size='sm'
                    w={80}
                  >
                    {weekDays[operatingDay.dayIndex]}
                  </Text>
                  <Text size='sm'>
                    {dayjs(`2000-01-01 ${operatingDay.startTime}`).format('hh:mm a')} -{' '}
                    {dayjs(`2000-01-01 ${operatingDay.endTime}`).format('hh:mm a')}
                  </Text>
                </Group>
              ) : null
            )}
          </Stack>
        )
      }
    )
  }

  return (
    <PropertyList
      items={items}
      {...props}
    />
  )
}
